import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { EllipsisIcon } from 'lucide-react';
import type { RedirectedProductListEntry } from 'contracts/redirected-products';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Popover, PopoverTrigger, PopoverContent } from 'components/ui/popover';
import { DataTableHelper, Formatter } from 'utils';

import { SapStatusIndicator } from '../components';

type Props = {
  showSelectionColumn?: boolean;
  onUpdateSapStatus?: (selectedProduct: RedirectedProductListEntry) => void;
  showActionsDropdown?: boolean;
}

const dt = new DataTableHelper<RedirectedProductListEntry>();

export const useRedirectedProductColumns = ({
                                              showSelectionColumn,
                                              onUpdateSapStatus,
                                              showActionsDropdown,
                                            }: Props): ColumnDef<RedirectedProductListEntry>[] => {
  const selection = dt.buildSelectionByRowColumn();
  const plant = dt.buildColumn({
    size: 120,
    id: 'Planta',
    accessorKey: 'plant',
    content: (row) => row.plant,
  });
  const destinationPlant = dt.buildColumn({
    id: 'Planta Destino',
    size: 120,
    accessorKey: 'destinationPlant',
    content: (row) => row.destinationPlant ?? '---',
  });
  const originalStorageLocation = dt.buildColumn({
    id: 'SLOC de',
    size: 120,
    accessorKey: 'originalStorageLocation',
    content: (row) => row.originalStorageLocation,
  });
  const storageLocation = dt.buildColumn({
    id: 'SLOC para',
    size: 120,
    accessorKey: 'storageLocation',
    content: (row) => row.storageLocation,
  });
  const material = dt.buildColumn({
    id: 'Código',
    size: 120,
    accessorKey: 'material',
    content: (row) => row.material,
  });
  const materialDescription = dt.buildColumn({
    id: 'Produto',
    size: 400,
    accessorKey: 'materialDescription',
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.materialDescription}
      >
        {row.materialDescription}
      </div>
    ),
  });
  const batch = dt.buildColumn({
    id: 'Lote',
    size: 120,
    accessorKey: 'batch',
    content: (row) => row.batch,
  });
  const expirationDate = dt.buildColumn({
    id: 'Dt. Venc.',
    size: 120,
    accessorKey: 'shelfLifeExpirationDate',
    content: (row) => row.shelfLifeExpirationDate
      ? Formatter.date(row.shelfLifeExpirationDate)
      : '---',
  });
  const createdAt = dt.buildColumn({
    id: 'Dt. Bloq.',
    size: 120,
    accessorKey: 'createdAt',
    content: (row) => Formatter.date(row.createdAt),
  });
  const amount = dt.buildColumn({
    id: 'Quantidate',
    size: 120,
    accessorKey: 'blockedStock',
    content: (row) => row.blockedStock || '---',
  });
  const usdValue = dt.buildColumn({
    id: 'Valor USD',
    size: 120,
    accessorKey: 'totalValue',
    content: (row) => row.totalValue
      ? Formatter.currency(row.totalValue, 'USD')
      : '---',
  });
  const estimation = dt.buildColumn<string>({
    id: 'Estimativa',
    size: 120,
    accessorKey: 'estimation',
    // TODO: calculate this on the API in a future implementation
    content: () => '---',
  });
  const reason = dt.buildColumn({
    id: 'Motivo',
    size: 400,
    accessorFn: (row) => row.productReasonType?.id,
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.productReasonType?.description}
      >
        {row.productReasonType?.description ?? '---'}
      </div>
    ),
  });
  const redirectionComment = dt.buildColumn({
    id: 'Observação ao redirecionar',
    size: 300,
    accessorFn: (row) => row.meta.redirectionComment,
    content: (row) => {
      const { redirectionComment } = row.meta;
      const showPopover = redirectionComment && redirectionComment.length > 30;

      return (
        <div className={'flex items-center'}>
          <div className={'max-w-[200px] truncate'}>
            {redirectionComment ?? '---'}
          </div>
          {showPopover && (
            <Popover>
              <PopoverTrigger onClick={(event) => event.stopPropagation()} className={'ml-auto'}>Ver
                mais</PopoverTrigger>
              <PopoverContent>{redirectionComment}</PopoverContent>
            </Popover>
          )}
        </div>
      );
    },
  });
  const redirectedBy = dt.buildColumn({
    id: 'Redirecionado por',
    size: 300,
    accessorFn: (row) => row.meta.redirectedBy?.email,
    content: (row) => {
      const { redirectedBy } = row.meta;
      if (!redirectedBy) return '---';

      return (
        <div className={'max-w-[300px] truncate'}>
          {redirectedBy.name}
        </div>
      );
    },
  });
  const sapStatusIndicator = dt.buildColumn({
    id: 'SapIndicator',
    size: 56,
    title: '',
    accessorKey: 'sapStatus',
    content: (row) => <SapStatusIndicator status={row.sapStatus} />,
    enableHiding: false,
    enableSorting: false,
  });
  const actions = dt.buildColumn<string>({
    id: 'Actions',
    size: 56,
    title: '',
    accessorKey: 'actions',
    content: (row) => {
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger onClick={(event) => {
            event.stopPropagation();
          }}>
            <EllipsisIcon className={'text-gray-600'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'end'}>
            <DropdownMenuItem onClick={(event) => {
              onUpdateSapStatus?.(row);
              event.stopPropagation();
            }}>
              Alterar status SAP
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    enableHiding: false,
    enableSorting: false,
  });

  return [
    ...(showSelectionColumn ? [selection] : []),
    plant,
    destinationPlant,
    originalStorageLocation,
    storageLocation,
    material,
    materialDescription,
    batch,
    expirationDate,
    createdAt,
    amount,
    usdValue,
    estimation,
    reason,
    redirectionComment,
    redirectedBy,
    sapStatusIndicator,
    ...(showActionsDropdown ? [actions] : []),
  ];
};